import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import { shippingSummary } from "../utils/shipping";
import "../css/faq.scss";

function Faq() {
  const contact = siteConfig.contact || {};
  const address = siteConfig.address?.formatted || contact.address;
  const shipping = siteConfig.shipping || {};

  const questions = [
    {
      id: "visit",
      question: `Can I visit the ${siteConfig.brandName} warehouse or shop in person?`,
      answer: (
        <>
          <p>
            Our Taringa location is a working warehouse and office rather than a
            walk-in retail shop. Order pickup is available only after our team
            confirms an appointment, so please contact us before making the
            trip.
          </p>
          {address && <p className="faq-page__note">Pickup location: {address}</p>}
        </>
      ),
    },
    {
      id: "domestic-shipping",
      question: "How much is standard shipping?",
      answer: (
        <>
          <p>
            {shippingSummary(shipping)} The exact charge appears in your
            order summary before you place the order.
          </p>
          <p>
            Orders are normally prepared in {shipping.processingTime || "1–2 business days"}.
            Carrier transit estimates begin after your package is collected.
          </p>
        </>
      ),
    },
    {
      id: "international-shipping",
      question: "Do you ship to Canada or destinations outside the U.S.?",
      answer: (
        <p>
          Checkout lists every currently supported destination. Countries and
          regions subject to applicable sanctions are unavailable. Contact our
          team if you need help confirming an eligible delivery destination.
        </p>
      ),
    },
    {
      id: "tracking",
      question: "How can I track my order?",
      answer: (
        <p>
          A production order should trigger a confirmation after checkout and a
          tracking link after carrier acceptance. This front-end demo shows a
          local order confirmation but does not send email or contact a carrier;
          those events require an order service integration.
        </p>
      ),
    },
    {
      id: "order-problem",
      question: "What if an item arrives damaged or I am not satisfied?",
      answer: (
        <p>
          Tell us promptly and include your order number, a short description,
          and photos when something arrived damaged or leaked. We review each
          concern personally and will arrange an appropriate replacement,
          credit, or refund. Please keep the item and packaging until our team
          replies.
        </p>
      ),
    },
    {
      id: "gifts",
      question: "Can I prepare an order as a gift?",
      answer: (
        <p>
          Yes. The checkout flow accepts a recipient delivery address and a
          short gift message. In this project those details remain part of the
          simulated local order; a production fulfillment service would use
          them to prepare and dispatch the parcel.
        </p>
      ),
    },
    {
      id: "checkout-security",
      question: "Is checkout secure?",
      answer: (
        <p>
          Yes. This site does not collect or retain card data. After reviewing
          an order, customers are redirected to the secure payment endpoint,
          where the payment provider handles sensitive payment details.
        </p>
      ),
    },
  ];

  return (
    <main className="content-page faq-page">
      <header className="page-hero page-hero--compact">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Customer care</p>
          <h1>Frequently asked questions</h1>
          <p className="page-hero__lede">
            Helpful details about visiting, shipping, gifts, and looking after
            your order.
          </p>
        </div>
      </header>

      <section className="page-section" aria-labelledby="faq-heading">
        <div className="page-shell faq-page__layout">
          <div className="faq-page__intro">
            <p className="eyebrow">Good to know</p>
            <h2 id="faq-heading">Answers from our pantry team.</h2>
            <p>
              Open a question for the details. If your situation is not covered,
              we are happy to help directly.
            </p>
            <div className="faq-page__contact">
              {contact.email && (
                <a href={`mailto:${contact.email}`}>{contact.email}</a>
              )}
            </div>
          </div>

          <div className="faq-page__list">
            {questions.map((item, index) => (
              <details key={item.id} id={`faq-${item.id}`} open={index === 0}>
                <summary>
                  <span className="faq-page__number" aria-hidden="true">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <span>{item.question}</span>
                </summary>
                <div className="faq-page__answer">{item.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="page-section page-section--tinted">
        <div className="page-shell faq-page__cta">
          <div>
            <p className="eyebrow">Still wondering?</p>
            <h2>Ask us about your order.</h2>
          </div>
          <Link className="button button--primary" to="/contact">
            Contact customer care
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Faq;
